import { useMemo } from 'react'

import { useEntityQuery, LegalEntity } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

type TravelRuleEntity = LegalEntity.CIRCLE_SG | LegalEntity.CIRCLE_FR

export const MORE_INFO_LINK_BY_ENTITY: Record<TravelRuleEntity, string> = {
  CIRCLE_SG:
    'https://help.circle.com/s/article/Circle-Mint-Singapore-Travel-Rule?category=Legal',
  CIRCLE_FR:
    'https://help.circle.com/s/article/Circle-Mint-France-Travel-Rule?category=Getting_Started',
}

const isTravelRuleEntity = (
  entity: string | undefined | null,
): entity is TravelRuleEntity =>
  entity != null && entity in MORE_INFO_LINK_BY_ENTITY

export const useTravelRuleRequired = () => {
  const { t } = useTranslation('common')
  const { data, loading, error } = useEntityQuery()

  const legalEntity = useMemo(
    () => data?.entity?.legalEntity,
    [data?.entity?.legalEntity],
  )

  const { jurisdiction, moreInfoLink } = useMemo(() => {
    /**
     * Only return translation string for travel rule entities.
     * This prevents a next-translate console error for entities that don't have an i18n string.
     */

    if (!isTravelRuleEntity(legalEntity))
      return { jurisdiction: '', moreInfoLink: '' }

    const jurisdiction = t(`travelRule.jurisdiction.${legalEntity}`)
    const moreInfoLink = MORE_INFO_LINK_BY_ENTITY[legalEntity]

    return {
      jurisdiction,
      moreInfoLink,
    }
  }, [legalEntity, t])

  const travelRuleSingaporeRequired = useMemo(() => {
    return legalEntity === LegalEntity.CIRCLE_SG
  }, [legalEntity])

  const travelRuleFranceRequired = useMemo(() => {
    return legalEntity === LegalEntity.CIRCLE_FR
  }, [legalEntity])

  const travelRuleRequired = useMemo(() => {
    return travelRuleSingaporeRequired || travelRuleFranceRequired
  }, [travelRuleFranceRequired, travelRuleSingaporeRequired])

  return {
    legalEntity,
    jurisdiction,
    moreInfoLink,
    travelRuleSingaporeRequired,
    travelRuleFranceRequired,
    travelRuleRequired,
    loading,
    error,
  }
}
